import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Activity, LogIn, ChevronRight, ChevronDown, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DailyTimeGrid, MemberDayBars, fmtMins } from "@/components/DailyTimeGrid";

const RANGES = [
  { v: "7d", label: "Last 7 days" },
  { v: "30d", label: "Last 30 days" },
  { v: "90d", label: "Last 90 days" },
  { v: "fy", label: "This FY" },
  { v: "all", label: "All time" },
];

const COLS = [
  { key: "leads", label: "Leads" },
  { key: "opportunities", label: "Opportunities" },
  { key: "opp_value", label: "Opp Value (INR Cr)" },
  { key: "calls", label: "Calls" },
  { key: "checkins", label: "Check-ins" },
  { key: "minutes", label: "Time on CRM" },
  { key: "today_minutes", label: "Today" },
  { key: "avg_minutes_per_active_day", label: "Avg / active day" },
];

const MEDAL = ["bg-amber-400 text-amber-950", "bg-slate-300 text-slate-800", "bg-orange-300 text-orange-950"];

function fmtCr(v) {
  if (!v) return "—";
  return (v / 10000000).toFixed(2);
}

function fmtLast(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function RankBadge({ rank }) {
  const medal = MEDAL[rank - 1];
  return (
    <span className={`inline-flex items-center justify-center h-6 min-w-[1.5rem] px-1.5 rounded-full text-xs font-bold font-mono ${medal || "bg-slate-100 text-slate-600"}`} data-testid={`activity-rank-${rank}`}>
      {medal ? <Trophy className="h-3 w-3 mr-0.5" /> : null}{rank}
    </span>
  );
}

export default function TeamActivity() {
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");
  const [withDealers, setWithDealers] = useState(false);
  const [sort, setSort] = useState("minutes");
  const [view, setView] = useState("summary");
  const [open, setOpen] = useState({});

  const viewAs = async (r) => {
    const res = await impersonate(r.user_id);
    if (!res.ok) toast.error(res.error);
    else navigate("/");
  };

  const params = { range, include_dealers: withDealers ? 1 : 0 };
  const { data, isLoading } = useQuery({
    queryKey: ["team-activity", range, withDealers],
    queryFn: async () => (await api.get("/team-activity", { params })).data,
  });
  const daily = useQuery({
    queryKey: ["team-activity-daily", range, withDealers],
    queryFn: async () => (await api.get("/team-activity/daily", { params })).data,
  });
  const dailyByUser = Object.fromEntries((daily.data?.members || []).map((m) => [m.user_id, m]));

  const rows = [...(data?.rows || [])].sort((a, b) => (b[sort] || 0) - (a[sort] || 0));
  const totals = COLS.reduce((acc, c) => ({ ...acc, [c.key]: rows.reduce((s, r) => s + (r[c.key] || 0), 0) }), {});
  const activeToday = rows.filter((r) => r.today_minutes > 0).length;
  const sortLabel = COLS.find((c) => c.key === sort)?.label;

  return (
    <div>
      <PageHeader title="Team Activity" subtitle="Who is creating leads & opportunities, logging calls, checking in on site — and how long they spend in the CRM.">
        <label className="flex items-center gap-2 text-xs text-slate-600 mr-1 cursor-pointer select-none">
          <Switch checked={withDealers} onCheckedChange={setWithDealers} data-testid="activity-include-dealers" /> Include dealers
        </label>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="activity-range"><SelectValue /></SelectTrigger>
          <SelectContent>{RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 -mt-3 mb-3">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="h-8">
            <TabsTrigger value="summary" className="text-xs h-7" data-testid="activity-view-summary">Ranking</TabsTrigger>
            <TabsTrigger value="daily" className="text-xs h-7" data-testid="activity-view-daily">Daily time</TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-xs text-slate-400" data-testid="activity-today-summary">
          {view === "summary"
            ? <>Ranked by <b className="text-slate-600">{sortLabel}</b> · <b className="text-slate-600">{activeToday}</b> of {rows.filter((r) => r.user_id !== "__former").length} active on the CRM today ({fmtMins(totals.today_minutes)} total). Click a column header to change the ranking; click ▸ on a row for its day-by-day time.</>
            : <>Minutes with the CRM open per person per day (IST). Darker = more time. Weekends shaded.</>}
        </p>
      </div>

      {view === "daily" ? <DailyTimeGrid data={daily.data} isLoading={daily.isLoading} /> : isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <Card className="border-slate-200 overflow-x-auto">
          <Table data-testid="team-activity-table">
            <TableHeader>
              <TableRow className="bg-slate-50/70">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-14">Rank</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Region</TableHead>
                {COLS.map((c) => (
                  <TableHead key={c.key} className={`text-xs uppercase tracking-wider text-right cursor-pointer select-none hover:text-slate-800 whitespace-nowrap ${sort === c.key ? "text-slate-900 font-bold" : "text-slate-500"}`}
                    onClick={() => setSort(c.key)} data-testid={`activity-sort-${c.key}`}>{c.label}{sort === c.key ? " ↓" : ""}</TableHead>
                ))}
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Last Active</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const idle = !r.leads && !r.opportunities && !r.calls && !r.checkins && !r.minutes;
                const former = r.user_id === "__former";
                const expanded = !!open[r.user_id];
                return (
                  <Fragment key={r.user_id}>
                    <TableRow className={idle ? "bg-amber-50/60" : ""} data-testid={`activity-row-${r.user_id}`}>
                      <TableCell className="py-2">{former ? null : <RankBadge rank={i + 1} />}</TableCell>
                      <TableCell className="font-medium text-slate-800 whitespace-nowrap max-w-[220px]" title={r.name}>
                        <div className="flex items-center gap-1.5">
                          {!former && (
                            <button onClick={() => setOpen((o) => ({ ...o, [r.user_id]: !o[r.user_id] }))} className="text-slate-400 hover:text-slate-700" title="Day-by-day time" data-testid={`activity-expand-${r.user_id}`}>
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                          {idle && <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" title="No activity in this period" />}
                          <span className="truncate">{r.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm whitespace-nowrap">{ROLE_LABELS[r.role] || r.role || "—"}</TableCell>
                      <TableCell className="text-slate-500 text-sm whitespace-nowrap">{r.region || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{r.leads || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{r.opportunities || "—"}</TableCell>
                      <TableCell className="text-right font-mono" data-testid={`activity-oppvalue-${r.user_id}`}>{fmtCr(r.opp_value)}</TableCell>
                      <TableCell className="text-right font-mono">{r.calls || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{r.checkins || "—"}</TableCell>
                      <TableCell className="text-right font-mono whitespace-nowrap font-semibold" data-testid={`activity-minutes-${r.user_id}`}>{fmtMins(r.minutes)}</TableCell>
                      <TableCell className={`text-right font-mono whitespace-nowrap ${r.today_minutes ? "text-emerald-700" : ""}`} data-testid={`activity-today-${r.user_id}`}>{fmtMins(r.today_minutes)}</TableCell>
                      <TableCell className="text-right font-mono whitespace-nowrap text-slate-600" data-testid={`activity-avg-${r.user_id}`}>
                        {fmtMins(r.avg_minutes_per_active_day)}{r.active_days ? <span className="text-[10px] text-slate-400 ml-1">({r.active_days}d)</span> : null}
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-500 whitespace-nowrap">{fmtLast(r.last_active)}</TableCell>
                      <TableCell className="text-right">
                        {!former && r.user_id !== user?.id && r.role !== "super_admin" && !user?.impersonated && (
                          <button onClick={() => viewAs(r)} title={`View the app as ${r.name}`}
                            className="text-indigo-500 hover:text-indigo-700 inline-flex items-center gap-1 text-xs font-medium"
                            data-testid={`activity-viewas-${r.user_id}`}>
                            <LogIn className="h-3.5 w-3.5" /> View as
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow className="bg-slate-50/60 hover:bg-slate-50/60" data-testid={`activity-detail-${r.user_id}`}>
                        <TableCell colSpan={4 + COLS.length + 2} className="p-0">
                          {daily.isLoading ? <p className="text-xs text-slate-400 px-3 py-2">Loading…</p>
                            : <MemberDayBars member={dailyByUser[r.user_id]} days={daily.data?.days || []} today={daily.data?.today} />}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              <TableRow className="bg-slate-50 font-semibold" data-testid="activity-totals-row">
                <TableCell />
                <TableCell className="flex items-center gap-2"><Activity className="h-3.5 w-3.5 text-slate-400" /> Total</TableCell>
                <TableCell /><TableCell />
                <TableCell className="text-right font-mono">{totals.leads}</TableCell>
                <TableCell className="text-right font-mono">{totals.opportunities}</TableCell>
                <TableCell className="text-right font-mono">{fmtCr(totals.opp_value)}</TableCell>
                <TableCell className="text-right font-mono">{totals.calls}</TableCell>
                <TableCell className="text-right font-mono">{totals.checkins}</TableCell>
                <TableCell className="text-right font-mono">{fmtMins(totals.minutes)}</TableCell>
                <TableCell className="text-right font-mono">{fmtMins(totals.today_minutes)}</TableCell>
                <TableCell /><TableCell /><TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
